/**
 * CommandPalette — a ⌘K / Ctrl-K launcher for fast navigation AND global search.
 *
 * Opens over any screen. Two layers in one keyboard-first list:
 *   1. LOCAL commands (no fetch): jump to a customer or a section of the current org.
 *   2. GLOBAL SEARCH (GET /api/v2/search, debounced): any device, client, or site across every
 *      org the operator can see — the single-pane "one search" that lands you on the entity's
 *      detail, wherever it lives. Selecting a hit switches org scope and navigates in.
 *
 * ⌘K/Ctrl-K toggles, ↑/↓ move, Enter runs, Esc closes. Navigation only — it never mutates
 * server state, so it is safe for every role (and search is org-scoped server-side).
 */
import { useEffect, useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useNavigate } from "react-router-dom";
import { useSession } from "@/stores/session";
import { useGlobalSearch } from "@/lib/api/hooks";
import { configHitsToCommands } from "@/lib/searchCommands";
import { QUARANTINE_LABEL, isQuarantineOrg, orgLabel } from "@/lib/quarantine";
import { AskPanel } from "./AskPanel";

/** The palette's two modes. "search" is the built keyboard-first list; "ask" is a stub seam for a
 *  future NL assistant (AskPanel). Both bodies stay MOUNTED so the ask lane can later stream in
 *  without disturbing search state -- only visibility toggles. */
type PaletteMode = "search" | "ask";

interface Command {
  id: string;
  label: string;
  hint?: string;
  run: () => void;
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  // Which tab is showing. Defaults to "search"; "ask" reveals the (stub) AskPanel. Both bodies
  // stay mounted -- see the render -- so switching tabs never tears down search state.
  const [mode, setMode] = useState<PaletteMode>("search");
  const navigate = useNavigate();

  const me = useSession((s) => s.me);
  const currentOrgId = useSession((s) => s.currentOrgId);
  const currentSiteId = useSession((s) => s.currentSiteId);
  const setOrg = useSession((s) => s.setOrg);

  // ⌘K / Ctrl-K toggles the palette from anywhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Debounce the query before it drives the search request, so a fast typist fires one call,
  // not one per keystroke. 200ms is below the perception threshold but well above key cadence.
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 200);
    return () => clearTimeout(t);
  }, [query]);
  const search = useGlobalSearch(debounced);

  // Build the command list from the session (no fetch).
  const commands = useMemo<Command[]>(() => {
    if (!me) return [];
    const list: Command[] = [];
    const managesMany = me.is_msp_admin || me.orgs.length > 1;

    if (managesMany) {
      list.push({ id: "nav-customers", label: "All customers", hint: "MSP portal", run: () => navigate("/customers") });
    }

    // Section jumps for the current org, honoring the active site scope.
    if (currentOrgId) {
      const base = currentSiteId
        ? `/o/${currentOrgId}/s/${currentSiteId}`
        : `/o/${currentOrgId}`;
      // Labels follow the Customer menu (lib/navModel). Overview is always customer-level (a
      // table of every site); Health is the explicit /dashboard path because the customer
      // index (/o/:org) now lands on the Overview.
      const sections: [string, string][] = [
        ["Overview", `/o/${currentOrgId}/overview`],
        ["Health", `${base}/dashboard`],
        ["Devices", `${base}/devices`],
        ["Clients", `${base}/clients`],
        ["Site services", `${base}/configure`],
        ["Templates", `${base}/templates`],
        ["Team", `${base}/operators`],
        ["Change log", `${base}/audit`],
      ];
      for (const [label, to] of sections) {
        list.push({ id: `nav-${label}`, label, hint: "Section", run: () => navigate(to) });
      }
    }

    // Jump to any customer (org) the operator can see.
    for (const org of me.orgs) {
      list.push({
        id: `org-${org.id}`,
        // The quarantine holding area is not a customer: label + hint say so (2026-10-19).
        label: orgLabel(org) ?? org.name,
        hint: isQuarantineOrg(org) ? "Holding area" : "Customer",
        run: () => {
          setOrg(org.id);
          navigate(`/o/${org.id}`);
        },
      });
    }
    return list;
  }, [me, currentOrgId, currentSiteId, navigate, setOrg]);

  // Global-search hits -> runnable commands. Each switches org scope then deep-links to the
  // entity in its own site, so a result found from anywhere lands you in the right place.
  const remoteCommands = useMemo<Command[]>(() => {
    const data = search.data;
    if (!data) return [];
    const jump = (orgId: number, to: string) => () => {
      setOrg(String(orgId));
      navigate(to);
    };
    const list: Command[] = [];
    // Hits inside the quarantine holding area say so instead of naming its placeholder site
    // as if it were a customer site (2026-10-19). Ids compare as strings (/me carries strings).
    const quarantineOrgs = new Set((me?.orgs ?? []).filter(isQuarantineOrg).map((o) => o.id));
    const inQuarantine = (orgId: number) => quarantineOrgs.has(String(orgId));
    for (const d of data.devices) {
      const base = d.site_id ? `/o/${d.org_id}/s/${d.site_id}` : `/o/${d.org_id}`;
      list.push({
        id: `dev-${d.serial}`,
        label: d.name,
        // The private IP rides in the hint: searching "192.168.1.102" should show WHY it hit.
        hint: `Device · ${inQuarantine(d.org_id) ? QUARANTINE_LABEL : (d.site_name ?? "Unassigned")}${d.private_ip ? ` · ${d.private_ip}` : ""}`,
        run: jump(d.org_id, `${base}/devices/${d.serial}`),
      });
    }
    for (const c of data.clients) {
      const base = c.site_id ? `/o/${c.org_id}/s/${c.site_id}` : `/o/${c.org_id}`;
      list.push({
        id: `cli-${c.mac}`,
        label: c.hostname ? `${c.hostname} · ${c.mac}` : c.mac,
        hint: `Client · ${c.device_name ?? "—"}`,
        // Deep-link to the "where is this MAC?" sheet, not the generic list.
        run: jump(c.org_id, `${base}/clients/${c.mac}`),
      });
    }
    for (const s of data.sites) {
      list.push({
        id: `site-${s.id}`,
        label: s.name,
        hint: inQuarantine(s.org_id) ? `Placeholder site · ${QUARANTINE_LABEL}` : "Site",
        run: jump(s.org_id, `/o/${s.org_id}/s/${s.id}/dashboard`),
      });
    }
    // Config (catalog) hits: SSID/RADIUS/ACL/... + Template. The pure mapper decides the
    // org-level route + label per kind (searchCommands); here we only attach the jump side
    // effect. Server already gates these to org-wide roles, so a confined role sees none.
    for (const cfg of configHitsToCommands(data.configs)) {
      list.push({
        id: cfg.id,
        label: cfg.label,
        hint: cfg.hint,
        run: jump(cfg.orgId, cfg.to),
      });
    }
    return list;
  }, [search.data, navigate, setOrg, me]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const local = !q
      ? commands
      : commands.filter(
          (c) => c.label.toLowerCase().includes(q) || (c.hint?.toLowerCase().includes(q) ?? false),
        );
    // Local nav first (cheap, exact), then live entity hits from the server.
    return [...local, ...remoteCommands];
  }, [commands, query, remoteCommands]);

  // Reset transient state whenever the palette opens/closes.
  useEffect(() => {
    if (!open) {
      setQuery("");
      setDebounced("");
      setActive(0);
      setMode("search");   // always reopen on the search tab
    }
  }, [open]);
  // Keep the active row in range as the filter narrows.
  useEffect(() => {
    setActive((a) => Math.min(a, Math.max(0, filtered.length - 1)));
  }, [filtered.length]);

  const runActive = () => {
    const cmd = filtered[active];
    if (cmd) {
      setOpen(false);
      cmd.run();
    }
  };

  const onListKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      runActive();
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-toast bg-ink/40" />
        <Dialog.Content
          className="fixed left-1/2 top-24 z-toast w-full max-w-lg -translate-x-1/2 rounded-lg border border-line bg-overlay shadow-sheet outline-none"
          aria-describedby={undefined}
        >
          <Dialog.Title className="sr-only">Command palette</Dialog.Title>

          {/* Mode tablist. "search" is the built list below; "ask" is a stub seam (AskPanel).
              Toggling only flips visibility -- both bodies stay mounted (see below). */}
          <div role="tablist" aria-label="Palette mode" className="flex gap-1 border-b border-line px-2 pt-2">
            {(["search", "ask"] as PaletteMode[]).map((m) => (
              <button
                key={m}
                type="button"
                role="tab"
                aria-selected={mode === m}
                onClick={() => setMode(m)}
                className={`rounded-t px-3 py-1.5 text-xs font-medium ${
                  mode === m ? "border-b-2 border-brand text-brand" : "text-ink-muted hover:text-ink"
                }`}
              >
                {m === "search" ? "Search" : "Ask"}
              </button>
            ))}
          </div>

          {/* SEARCH body -- kept MOUNTED and merely `hidden` when Ask is active, so search state
              (query, active row) survives a tab switch. Arrow/Enter nav is scoped HERE: while
              hidden this subtree is inert, so keystrokes in Ask mode never move the search cursor. */}
          <div hidden={mode !== "search"} onKeyDown={onListKey}>
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search devices, clients, sites… or jump to a section"
              className="w-full border-b border-line bg-transparent px-4 py-3 text-ink outline-none placeholder:text-ink-subtle"
            />
            <ul className="max-h-80 overflow-y-auto p-1">
              {filtered.length === 0 && (
                <li className="px-3 py-6 text-center text-sm text-ink-muted">
                  {search.isFetching
                    ? "Searching…"
                    : debounced.length >= 2
                      ? "No matches."
                      : "Type at least 2 characters to search."}
                </li>
              )}
              {filtered.map((cmd, i) => (
                <li key={cmd.id}>
                  <button
                    type="button"
                    onMouseEnter={() => setActive(i)}
                    onClick={() => {
                      setOpen(false);
                      cmd.run();
                    }}
                    className={`flex w-full items-center justify-between gap-3 rounded px-3 py-2 text-left text-sm ${
                      i === active ? "bg-brand-soft text-brand" : "text-ink hover:bg-surface-2"
                    }`}
                  >
                    <span className="truncate">{cmd.label}</span>
                    {cmd.hint && <span className="shrink-0 text-xs text-ink-subtle">{cmd.hint}</span>}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* ASK body -- also always mounted (stub today; a streamed AI answer later can fill
              AskPanel without touching any of the search logic above). */}
          <div hidden={mode !== "ask"}>
            <AskPanel />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
